import {
  put,
  take,
  takeLeading,
  takeLatest,
  select,
  call,
  race,
} from 'redux-saga/effects';
import _ from 'lodash';
import Bugsnag from '@bugsnag/js';
import notifyBugsnag from '../../../../../util/notifyBugsnag';
import parseCartValidatorError from '../../../../../util/parseCartValidatorError';
import parseStaleCheckoutError from '../../../../../util/parseStaleCheckoutError';
import {
  canAttemptStaleRecovery,
  markStaleRecoveryAttempt,
  notifyStaleRecovered,
  notifyStaleRetryExhausted,
  __resetStaleRecoveryForTests as __resetSharedStaleRecovery,
} from '../../../../../util/staleCheckoutRecovery';

import Notification from '../../../../../components/layout/Notification';
import { formatCartForApi } from '../../../../../util/Helpers';
import * as orderActions from '../../../reducers/ducks/legacy/OrderDuck';
import * as mainActions from '../../../reducers/ducks/legacy/MainDuck';
import {
  checkoutAdjust as restaurantApiCheckoutAdjust,
  checkoutEnd as restaurantApiCheckoutEnd,
  checkoutStart as restaurantApiCheckoutStart,
  getOrderCart,
  resetOrderCart,
  setOrderCart,
} from '../../../../../util/api/RestaurantApiClient';
import { getTimeParams } from '../../../../../util/Restaurant';
import { deliveryAddressError } from '../../../../../util/deliveryAddress';
import resolveDeliveryAddress from '../../../../../util/deliveryAddressService';
import errorMessages from '../../../../../constants/errorMessages';
import accountApi from '../../../../../util/api/AccountApiClient';
import {
  setCartResponse,
  updateCartStatus,
} from '../../../reducers/ducks/legacy/OrderDuck';
import { setSelectedAddress } from '../../../reducers/ducks/new/Address/AddressReducer';
import { setGCAvailableTrigger } from '../../../reducers/ducks/legacy/GiftDuck';
import { updatePhones } from '../../../reducers/ducks/legacy/ProfileDuck';
import * as authActions from '../../../reducers/ducks/legacy/AuthorizationDuck';
import orderTimingGuard from './orderTimingGuard';
import { getOrderTiming } from '../../../../../util/orderTiming';
import { bindCheckoutTime } from '../../../reducers/ducks/new/Time/TimeReducer';
import { createCheckoutAttemptContext } from '../../../../../util/checkoutContext';
import watchCheckoutContext from './watchCheckoutContext';
import {
  checkoutProgress,
  nextCheckoutAttempt,
} from '../../../../../util/checkoutProgress';

// eslint-disable-next-line no-underscore-dangle
export const __resetStaleRecoveryForTests = __resetSharedStaleRecovery;

const buildCartSnapshot = (formattedCart, menuItems = null) => {
  if (!Array.isArray(formattedCart) || !formattedCart.length) {
    return {
      totalItemGroups: 0,
      totalItems: 0,
      hasModifierGap: false,
      cart: [],
    };
  }
  const cart = formattedCart.map((group) => ({
    menuItemId: group.id,
    items: (group.items || []).map((item) => ({
      sort: item.sort,
      quantity: item.quantity,
      answerCount: (item.answers || []).length,
      answers: (item.answers || []).map((a) => ({
        answerId: a.answer,
        amount: a.amount ?? 1,
      })),
    })),
  }));
  const hasModifierGap = cart.some((g) => {
    const menuItem = menuItems ? menuItems[g.menuItemId] : null;
    const isModifierCapable = menuItem
      ? Object.keys(menuItem.extras || {}).length > 0
      : false;
    return isModifierCapable && g.items.some((i) => i.answerCount === 0);
  });
  return {
    totalItemGroups: cart.length,
    totalItems: cart.reduce((acc, g) => acc + g.items.length, 0),
    hasModifierGap,
    cart,
  };
};

/**
 * @returns {Array<{
 *   menuItemId: number;
 *   sort: number;
 *   quantity: number;
 *   comment: string;
 *   extras: {
 *     extraId: {
 *       id: number;
 *       items: Array<{
 *         id: number;
 *         quantity: number;
 *       }>;
 *     };
 *   };
 * }>}
 */
const formatCartForStore = (apiCart, menuItems) => {
  const cart = [];

  apiCart.forEach((cartItem) => {
    const menuItem = menuItems[cartItem.id];
    const items = cartItem.items.map((item) => {
      const extras = {};
      (item.answers || []).forEach((answer) => {
        const extraItem = {
          id: answer.answer,
          quantity: answer.amount || 1,
          extraId: null,
          parentId: null,
          sort: menuItem.options[answer.answer]?.sort,
        };

        if (answer.parent) {
          extraItem.id = answer.parent;
          extraItem.parentId = answer.answer;
          extraItem.sort = menuItem.options[answer.parent]?.sort;
        }

        const extra = menuItem.extras[menuItem.options[extraItem.id].parent];
        extraItem.extraId = extra.id;
        extraItem.order = extra.order;

        extras[extraItem.id] = extraItem;
      });

      return {
        menuItemId: cartItem.id,
        sort: item.sort,
        quantity: item.quantity || 1,
        comment: item.special_instructions || '',
        extras,
      };
    });

    cart.push(...items);
  });

  return cart.sort((a, b) => (a.sort > b.sort ? 1 : -1));
};

function* isCurrentCheckout(expectedKey, expectedId) {
  const current = yield select((state) => ({
    key: getOrderTiming(state).key,
    id: state.order.checkoutData?.id,
  }));
  return (
    current.key === expectedKey &&
    current.id === expectedId &&
    (yield call(orderTimingGuard, true, expectedKey))
  );
}

export function* checkoutAdjustSaga({ payload }) {
  if (!(yield call(orderTimingGuard, true))) return;
  const timing = yield select((state) => getOrderTiming(state));
  const { selectedRestaurant, checkoutData, coupons, cartItems, menuItems } =
    yield select(({ restaurant, restaurants, order }) => ({
      selectedRestaurant: restaurant.selectedRestaurantId,
      checkoutData: order.checkoutData,
      coupons: order.coupons,
      cartItems: order.cartItems,
      menuItems: restaurants.menuItems,
    }));

  try {
    const clientId = selectedRestaurant || checkoutData?.restaurant;

    const requestBody = {
      client: clientId,
      checkout: checkoutData?.id,
      data: payload,
    };

    const recalcWriter =
      Object.keys(payload || {}).length > 0
        ? 'consumer.checkout-recalc-with-context'
        : 'consumer.checkout-recalc';
    const normalizedItems = (cartItems || []).map((i) => ({
      ...i,
      answers: Array.isArray(i.answers) ? i.answers : [],
    }));
    Bugsnag.leaveBreadcrumb(
      'consumer.checkout-recalc: cart snapshot',
      {
        writer: recalcWriter,
        checkoutId: checkoutData?.id ?? null,
        checkoutCode: checkoutData?.code ?? null,
        ...buildCartSnapshot(formatCartForApi(normalizedItems), menuItems),
      },
      'state'
    );

    const newCheckoutData = yield call(
      restaurantApiCheckoutAdjust,
      requestBody
    );
    if (!(yield call(isCurrentCheckout, timing.key, checkoutData?.id))) return;

    const responseSummaryItems = newCheckoutData?.summary?.items;
    if (
      Array.isArray(responseSummaryItems) &&
      responseSummaryItems.length > 0
    ) {
      const localCartSortIndex = {};
      (cartItems || []).forEach((item) => {
        const key = `${item.menuItemId}:${item.sort}`;
        localCartSortIndex[key] = true;
      });

      const driftedSummaryItems = responseSummaryItems.filter((si) => {
        const entityId = si.entity_id ?? si.entityId;
        return !localCartSortIndex[`${entityId}:${si.sort}`];
      });

      if (driftedSummaryItems.length > 0) {
        const snapshotOrigin = newCheckoutData?.snapshot_origin ?? 'unknown';

        notifyBugsnag(
          'consumer.checkout-recalc: sort-drift detected — cart resynced',
          {
            sortDrift: {
              checkoutId: checkoutData?.id ?? null,
              snapshotOrigin,
              driftedItemCount: driftedSummaryItems.length,
              driftedItems: driftedSummaryItems.map((si) => ({
                entityId: si.entity_id ?? si.entityId,
                summarySortAfterAdjust: si.sort,
              })),
              localCartSortsBefore: (cartItems || []).map((item) => ({
                menuItemId: item.menuItemId,
                sort: item.sort,
              })),
            },
          }
        );
      }
    }

    yield put(
      orderActions.setCheckoutData({
        ...checkoutData,
        ...newCheckoutData,
      })
    );

    yield put(orderActions.setOrderFees(newCheckoutData.fees));
    if (!_.isEmpty(coupons[0])) {
      yield put(
        orderActions.setActiveCoupon({ isValid: true, coupon: coupons[0] })
      );
    }

    yield put(orderActions.setCheckoutAdjustResponse(newCheckoutData));
  } catch (err) {
    if (!(yield call(isCurrentCheckout, timing.key, checkoutData?.id))) return;
    const errorMessage = err?.data?.message || err?.message;
    const staleInfo = parseStaleCheckoutError(err);
    const staleCheckoutId = staleInfo ? checkoutData?.id ?? null : null;
    let staleHandled = false;

    if (staleInfo) {
      if (canAttemptStaleRecovery(staleCheckoutId)) {
        markStaleRecoveryAttempt(staleCheckoutId);
        Notification.info('Refreshing your checkout\u2026', {
          toastId: 'CheckoutStaleRetry',
        });
        yield put(orderActions.setCheckoutData(null));
        try {
          // eslint-disable-next-line no-use-before-define
          yield call(checkoutStart);
          const freshCheckoutData = yield select(
            ({ order }) => order.checkoutData
          );
          if (freshCheckoutData?.id) {
            if (!(yield call(orderTimingGuard, true, timing.key))) return;
            const retryBody = {
              client: selectedRestaurant || freshCheckoutData.restaurant,
              checkout: freshCheckoutData.id,
              data: payload,
            };
            const retried = yield call(restaurantApiCheckoutAdjust, retryBody);
            if (
              !(yield call(isCurrentCheckout, timing.key, freshCheckoutData.id))
            )
              return;
            yield put(
              orderActions.setCheckoutData({
                ...freshCheckoutData,
                ...retried,
              })
            );
            yield put(orderActions.setOrderFees(retried.fees));
            if (!_.isEmpty(coupons[0])) {
              yield put(
                orderActions.setActiveCoupon({
                  isValid: true,
                  coupon: coupons[0],
                })
              );
            }
            yield put(orderActions.setCheckoutAdjustResponse(retried));
            notifyStaleRecovered({
              checkoutId: staleCheckoutId,
              source: 'checkout/adjust',
              status: staleInfo.status,
              errorCode: staleInfo.errorCode,
            });
            return;
          }
        } catch (recoveryErr) {
          notifyBugsnag(recoveryErr);
        }
      } else {
        notifyStaleRetryExhausted({
          checkoutId: staleCheckoutId,
          source: 'checkout/adjust',
          status: staleInfo.status,
          errorCode: staleInfo.errorCode,
        });
      }
      staleHandled = true;
    }

    yield put(
      orderActions.setCheckoutAdjustResponse({
        error: true,
        errorMessage,
      })
    );

    if (!staleHandled) {
      notifyBugsnag(err);
    }

    Notification.error(errorMessage || 'Error while adjusting the checkout', {
      toastId: 'ErrorWhileAdjustingTheCheckout',
    });
  }
}

function* checkoutStartAttempt() {
  let context;
  let checkoutContext;
  let contextCurrent = true;
  let deliveryRequest = false;
  try {
    while (yield select((state) => state.authorization?.isLoading)) {
      yield take(authActions.updateLoadingStatus.type);
    }
    if (!(yield call(orderTimingGuard))) return;
    checkoutContext = createCheckoutAttemptContext(
      yield select((state) => state)
    );
    const timing = yield select((state) => getOrderTiming(state));
    context = yield select(checkoutContext);
    const {
      selectedRestaurant,
      cartItems,
      type,
      time,
      address,
      phone,
      phones,
      user,
    } = yield select(
      ({
        order,
        restaurant,
        profile,
        address: selectedAddress,
        time: selectedTime,
        main,
      }) => ({
        selectedRestaurant: restaurant?.selectedRestaurantId,
        checkoutData: order.checkoutData,
        cartItems: order.cartItems,
        type: order.type,
        time: selectedTime.selectedTime,
        address: selectedAddress.selectedAddress,
        phone: order.phone,
        phones: profile.phones,
        user: main.user,
      })
    );

    const cart = formatCartForApi(cartItems);

    if (!cart || !cart.length) {
      yield put(
        mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
      );
      Notification.error(
        'Your cart is empty. Please add items before checking out.',
        { toastId: 'EmptyCartError' }
      );
      return;
    }

    let phoneId;

    if (phone) {
      phoneId = phones?.find((i) => i.number === phone)?.id;
    }

    if (!phoneId) {
      const fallbackPhone = phones?.find((i) => i.primary) ?? phones?.[0];
      phoneId = fallbackPhone?.id;
      yield put(orderActions.setPhone(fallbackPhone?.number));
    }

    if (!phoneId) {
      const refreshedPhones = yield call(accountApi.phone.getAllPhones);
      if (refreshedPhones?.length > 0) {
        const refreshed =
          refreshedPhones.find((i) => i.primary) ?? refreshedPhones[0];
        phoneId = refreshed?.id;
        yield put(orderActions.setPhone(refreshed?.number));
        yield put(updatePhones(refreshedPhones));
      }
    }

    if (!phoneId) {
      yield put(orderActions.setPhoneRequired(true));
      yield put(
        mainActions.toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      return;
    }

    const requestBody = {
      client: selectedRestaurant,
      data: {
        mode: type.value,
        phone: phoneId,
        ...getTimeParams(time),
        cart,
      },
    };
    deliveryRequest = type.value === 'delivery';
    if ((yield select(checkoutContext)) !== context) return;

    if (type.value === 'delivery') {
      try {
        const resolution = yield race({
          resolved: call(
            resolveDeliveryAddress,
            address,
            user?.id,
            () => contextCurrent
          ),
          changed: call(watchCheckoutContext, context, checkoutContext),
        });
        if (resolution.changed) {
          contextCurrent = false;
          yield put(
            mainActions.toggleGlobalLoaderQueue({
              name: 'checkout',
              status: false,
            })
          );
          return;
        }
        const { resolved } = resolution;
        const current = yield select((state) => state);
        if (
          checkoutContext(current) !== context ||
          current.main.user?.id !== user?.id ||
          current.order.type?.value !== type.value
        ) {
          return;
        }
        if (!resolved.address_id || !user?.id)
          throw new Error('Sign in required');
        yield put(setSelectedAddress(resolved));
        requestBody.data.address = resolved.address_id;
      } catch (addressErr) {
        if ((yield select(checkoutContext)) !== context) return;
        Notification.error(deliveryAddressError(addressErr), {
          toastId: 'delivery-address-confirm',
        });
        yield put(orderActions.setAddressRequired(true));
        yield put(
          mainActions.toggleGlobalLoaderQueue({
            name: 'checkout',
            status: false,
          })
        );
        return;
      }
    }

    const beforeRequest = yield select((state) => getOrderTiming(state));
    if (
      (yield select(checkoutContext)) !== context ||
      beforeRequest.key !== timing.key ||
      !(yield call(orderTimingGuard))
    )
      return;
    const newCheckoutData = yield call(restaurantApiCheckoutStart, requestBody);
    const afterRequest = yield select((state) => getOrderTiming(state));
    if (
      (yield select(checkoutContext)) !== context ||
      afterRequest.key !== timing.key ||
      afterRequest.requiresConfirmation
    ) {
      yield put(
        mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
      );
      return;
    }
    newCheckoutData.restaurant = selectedRestaurant;
    newCheckoutData.noFees = {
      total: newCheckoutData.total,
      subtotal: newCheckoutData.subtotal,
    };

    const dynamicFeesValues = {};
    if (newCheckoutData?.fees) {
      newCheckoutData.fees.forEach((fee) => {
        if ((fee.adjustable || fee.adjustable_percentages) && fee.percent) {
          dynamicFeesValues[fee.id] = fee.percent;
        }
      });
    }

    yield put(orderActions.setDynamicFeesValues(dynamicFeesValues));

    yield put(bindCheckoutTime({ id: newCheckoutData.id, key: timing.key }));
    yield put(orderActions.setCheckoutData(newCheckoutData));
    yield put(orderActions.setOrderFees(newCheckoutData.fees));
    yield put(setGCAvailableTrigger(true));
    // eslint-disable-next-line consistent-return
    return 'success';
  } catch (err) {
    if (context && (yield select(checkoutContext)) !== context) return;
    const errMsg = err?.data?.message || err?.message || '';
    const isNoAddressError = errMsg.toLowerCase().includes('had no address');

    if (deliveryRequest && isNoAddressError) {
      // A checkout write may have succeeded before returning an error. Do not
      // create another address, change the primary, or blindly replay it.
      Notification.error(
        'Checkout could not confirm the delivery address. Please review your address and try checkout again.',
        {
          toastId: 'delivery-address-confirm',
        }
      );
      yield put(orderActions.setAddressRequired(true));
      yield put(
        mainActions.toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      return;
    }

    yield put(orderActions.setCheckoutData(null));

    yield put(
      mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
    );

    const isMinimumError = errMsg.toLowerCase().includes('minimum');
    if (isMinimumError) {
      Notification.warning(
        'Your cart total does not meet the minimum order amount. Please add more items.',
        { toastId: 'ErrorWhileStartingTheCheckout' }
      );
    } else {
      notifyBugsnag(err);
      Notification.error(errMsg || 'Error while starting the checkout', {
        toastId: 'ErrorWhileStartingTheCheckout',
      });
    }
  } finally {
    contextCurrent = false;
  }
}

export function* checkoutStart() {
  // Wait for authentication before capturing the signed-in context.
  while (yield select((state) => state.authorization?.isLoading)) {
    yield take(authActions.updateLoadingStatus.type);
  }
  const checkoutContext = createCheckoutAttemptContext(
    yield select((state) => state)
  );
  const context = yield select(checkoutContext);
  const attemptId = nextCheckoutAttempt();
  checkoutProgress('checkout-start', attemptId, 'pending');
  yield put(
    orderActions.setCheckoutStartResult({ attemptId, outcome: 'pending' })
  );
  try {
    const result = yield race({
      completed: call(checkoutStartAttempt),
      changed: call(watchCheckoutContext, context, checkoutContext),
    });
    let outcome = 'blocked';
    if (result.completed === 'success') outcome = 'success';
    if (result.changed) outcome = 'cancelled';
    checkoutProgress('checkout-start', attemptId, outcome);
    yield put(orderActions.setCheckoutStartResult({ attemptId, outcome }));
    if (result.changed) {
      Notification.error(
        'Your order details changed. Review your cart and try checkout again.',
        {
          toastId: 'checkout-context-changed',
        }
      );
    }
  } finally {
    yield put(
      mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
    );
  }
}

export function* checkoutEnd({ payload }) {
  if (!(yield call(orderTimingGuard, true))) return;
  const submissionTiming = yield select((state) => getOrderTiming(state));
  const { selectedRestaurant, checkoutData } = yield select(
    ({ order, restaurant }) => ({
      checkoutData: order.checkoutData,
      selectedRestaurant: restaurant.selectedRestaurantId,
    })
  );
  const clientId = selectedRestaurant || checkoutData?.restaurant;

  try {
    const requestBody = {
      client: clientId,
      checkout: checkoutData.id,
      payment: payload.payment,
      data: payload.data,
    };

    yield call(restaurantApiCheckoutEnd, requestBody);
    yield call(resetOrderCart, clientId);

    if (payload && payload?.callback) {
      payload.callback();
    }

    yield put(orderActions.setCheckoutData({ ...checkoutData, isEnded: true }));
  } catch (err) {
    const staleInfo = parseStaleCheckoutError(err);
    const staleCheckoutId = staleInfo ? checkoutData?.id ?? null : null;
    let staleHandled = false;
    if (staleInfo) {
      if (canAttemptStaleRecovery(staleCheckoutId)) {
        markStaleRecoveryAttempt(staleCheckoutId);
        Notification.info('Refreshing your checkout\u2026', {
          toastId: 'CheckoutStaleRetry',
        });
        yield put(orderActions.setCheckoutData(null));
        try {
          yield call(checkoutStart);
          const freshCheckoutData = yield select(
            ({ order }) => order.checkoutData
          );
          if (freshCheckoutData?.id) {
            // Recovery may await a new checkout while the customer changes
            // time. Never replay this submission for a different intent.
            if (!(yield call(orderTimingGuard, true, submissionTiming.key)))
              return;
            const retryBody = {
              client:
                selectedRestaurant || freshCheckoutData.restaurant || clientId,
              checkout: freshCheckoutData.id,
              payment: payload.payment,
              data: payload.data,
            };
            yield call(restaurantApiCheckoutEnd, retryBody);
            yield call(
              resetOrderCart,
              selectedRestaurant || freshCheckoutData.restaurant || clientId
            );
            if (payload && payload?.callback) {
              payload.callback();
            }
            yield put(
              orderActions.setCheckoutData({
                ...freshCheckoutData,
                isEnded: true,
              })
            );
            notifyStaleRecovered({
              checkoutId: staleCheckoutId,
              source: 'checkout/end',
              status: staleInfo.status,
              errorCode: staleInfo.errorCode,
            });
            return;
          }
        } catch (recoveryErr) {
          notifyBugsnag(recoveryErr);
        }
      } else {
        notifyStaleRetryExhausted({
          checkoutId: staleCheckoutId,
          source: 'checkout/end',
          status: staleInfo.status,
          errorCode: staleInfo.errorCode,
        });
      }
      staleHandled = true;
    }

    if (!staleHandled) {
      notifyBugsnag(err);
    }

    Notification.error(
      err?.data?.message || err?.message || 'Error while ending the checkout',
      {
        toastId: 'ErrorWhileEndingTheCheckout',
      }
    );
  }
}

export function* placeOrder({ payload }) {
  if (!(yield call(orderTimingGuard, true))) return;
  try {
    yield put(
      mainActions.toggleGlobalLoaderQueue({
        name: 'checkout',
        status: true,
      })
    );
    yield put(
      orderActions.checkoutEnd({
        data: {},
        payment: 'cash',
      })
    );

    if (payload.callback) {
      payload.callback();
    }
  } catch (error) {
    payload.showError('Failed to place order.');
  }
}

export function* setCart({ payload }) {
  const checkoutContext = createCheckoutAttemptContext(
    yield select((state) => state)
  );
  const context = yield select(checkoutContext);
  const complete = (result) =>
    setCartResponse({
      ...result,
      attemptId: payload.attemptId,
      context,
    });
  const { user, menuItems, selectedTime } = yield select(
    ({ main, restaurants, time }) => ({
      user: main.user,
      menuItems: restaurants.menuItems,
      // selectedTime starts as null until restaurant details arrive and a time
      // slot is resolved.  Guard below prevents sending a cart request with an
      // unresolved time.
      selectedTime: time.selectedTime,
    })
  );

  try {
    if (user && payload.isCheckoutStart) {
      if (!(yield call(orderTimingGuard))) {
        yield put(complete({ error: 'Confirm your order time to continue.' }));
        yield put(updateCartStatus(false));
        return;
      }
      // Do not dispatch the cart API request while the delivery time has not
      // been resolved yet (selectedTime is null at cold-start, before the
      // restaurant details load and a slot is chosen).
      if (selectedTime === null) {
        yield put(
          complete({
            error: 'Please choose your order time and try checkout again.',
          })
        );
        yield put(updateCartStatus(false));
        return;
      }
      Bugsnag.leaveBreadcrumb(
        'consumer.setCart: cart snapshot',
        {
          writer: 'consumer.setCart',
          ...buildCartSnapshot(payload.apiData?.data?.cart || [], menuItems),
        },
        'state'
      );
      const result = yield race({
        orderCart: call(setOrderCart, payload.apiData),
        changed: call(watchCheckoutContext, context, checkoutContext),
      });
      if (result.changed || (yield select(checkoutContext)) !== context) {
        checkoutProgress('cart-save', payload.attemptId, 'cancelled');
        yield put(
          complete({
            error:
              'Your order details changed. Review your cart and try checkout again.',
            cancelled: true,
          })
        );
        yield put(updateCartStatus(false));
        return;
      }
      const { orderCart } = result;
      yield put(updateCartStatus(true));
      checkoutProgress('cart-save', payload.attemptId, 'success');
      yield put(complete({ response: orderCart }));
      if (payload?.adjust) {
        yield call(checkoutAdjustSaga, { payload: {} });
      }
    } else if (payload.isCheckoutStart) {
      yield put(
        complete({ error: 'Please sign in again to continue checkout.' })
      );
    }
  } catch (err) {
    if (payload.isCheckoutStart && (yield select(checkoutContext)) !== context)
      return;
    // Try the structured /cart/set validator response first (paired with the
    // Eatzy API task that ships a structured 400 + request logging for
    // RestaurantCustomerSetCartTask). When present, we route to a distinct
    // Bugsnag error class with structured metadata and show a customer-
    // friendly toast instead of the unfriendly raw "Cart array not valid".
    const validatorError = parseCartValidatorError(err);

    if (validatorError) {
      Bugsnag.notify(
        new Error('consumer.setCart: validator rejected'),
        (event) => {
          // eslint-disable-next-line no-param-reassign
          event.severity = 'warning';
          event.addMetadata('cartValidator', {
            reasonCode: validatorError.reasonCode,
            field: validatorError.field,
            itemId: validatorError.itemId,
            itemName: validatorError.itemName,
            sort: validatorError.sort,
            detail: validatorError.detail,
            friendlyMessage: validatorError.friendlyMessage,
          });
          event.addMetadata('apiResponse', {
            status: err?.status,
            statusText: err?.statusText,
            url: err?.config?.url,
            method: err?.config?.method,
            errorData: validatorError.raw,
          });
        }
      );

      yield put(complete({ error: validatorError.friendlyMessage }));
      Notification.error(validatorError.friendlyMessage, {
        toastId: `cartValidator:${validatorError.reasonCode || 'unknown'}`,
      });
      yield put(updateCartStatus(false));
      return;
    }

    notifyBugsnag(err);

    let errorMessage = err?.data?.message || err?.message;
    errorMessage = errorMessage?.includes('not found')
      ? errorMessages.ITEM_NOT_AVAILABLE
      : errorMessage;
    yield put(
      complete({
        error:
          errorMessage ||
          'Could not save your cart. Please try checkout again.',
      })
    );

    const { selectedTimeForError } = yield select(({ time }) => ({
      selectedTimeForError: time.selectedTime,
    }));
    const isAsapSelected = selectedTimeForError?.type === 'asap';
    const lowerMsg =
      typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
    const isTimeAvailabilityError =
      lowerMsg.includes('not available') &&
      (lowerMsg.includes('date') ||
        lowerMsg.includes('time') ||
        lowerMsg.includes('at selected'));
    if (!isAsapSelected || !isTimeAvailabilityError) {
      Notification.error(errorMessage, {
        toastId: err?.data?.message,
      });
    }

    yield put(updateCartStatus(false));
  } finally {
    yield put(updateCartStatus(false));
    if (payload.payloadCartData?.sort) {
      yield put(
        orderActions.removeUpdatingCartItems(payload.payloadCartData.sort)
      );
    }
  }
}

export function* getCart() {
  try {
    const { user, selectedRestaurant, cartRestaurant, menuItems } =
      yield select(({ main, restaurants, order }) => ({
        user: main.user,
        selectedRestaurant: restaurants.selectedRestaurant,
        cartRestaurant: order.cartRestaurant,
        menuItems: restaurants.menuItems,
      }));

    if (cartRestaurant !== selectedRestaurant) {
      yield put(orderActions.setDynamicFeesValues({}));
      yield call(setCart, { payloadCartData: { cart: [] } });
      return;
    }

    let cartFromApi = [];
    if (user) {
      const response = yield call(getOrderCart, selectedRestaurant);
      cartFromApi = formatCartForStore(response?.cart || [], menuItems);
    }

    yield put(orderActions.setCartItems(cartFromApi));
  } catch (err) {
    notifyBugsnag(err);

    Notification.error(
      err?.data?.message || err?.message || 'Error while getting the cart',
      {
        toastId: 'ErrorWhileGettingTheCart',
      }
    );
  }
}

export function* watchOrderSagas() {
  yield takeLeading(orderActions.getCart.type, getCart);
  yield takeLeading(orderActions.setCart.type, setCart);
  yield takeLeading(orderActions.checkoutStart.type, checkoutStart);
  yield takeLatest(
    orderActions.setCheckoutAdjustRequest.type,
    checkoutAdjustSaga
  );
  yield takeLeading(orderActions.checkoutEnd.type, checkoutEnd);
  yield takeLeading(orderActions.placeOrder.type, placeOrder);
}
